import React, { useState, useEffect, useCallback } from 'react';
import './StockAnalyzer.css';
import Keyboard from 'react-simple-keyboard';
import 'react-simple-keyboard/build/css/index.css';
import toast, { Toaster } from 'react-hot-toast';
import MobileDatePicker from './MobileDatePicker';
import PeriodDayPicker from './PeriodDayPicker';
import axios from 'axios';

import { ReactComponent as LogoIcon } from '../assets/logo.svg';
import { ReactComponent as ArrowDownIcon } from '../assets/arrow-down.svg';
import { ReactComponent as ArrowUpIcon } from '../assets/arrow-up.svg';
import CardTopSvg from './CardTopSvg';
import CardBottomSvg from './CardBottomSvg';
import EasterEggPage from './EasterEggPage';

// SVG 图标

const CalendarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="4" width="14" height="12" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
    <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2"/>
    <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2"/>
    <line x1="3" y1="10" x2="17" y2="10" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

const CursorIcon = () => (
  <svg width="2" height="20" viewBox="0 0 2 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="1" y1="0" x2="1" y2="20" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <animate attributeName="opacity" values="1;0;1" dur="1s" repeatCount="indefinite"/>
    </line>
  </svg>
);

interface AnalysisResult {
  stock_symbol: string;
  stock_name: string;
  investment_mode: string;
  investment_amount: number;
  purchase_year: number;
  purchase_date: string;
  purchase_price: number;
  current_date: string;
  current_price: number;
  current_stock_value: number;
  shares_bought: number;
  total_return_rate: number;
  total_dividends: number;
  dividend_data_status: string;
  dividend_status_message?: string;
  years_held: number;
  purchase_records?: Array<{
    date: string;
    price: number;
    amount: number;
    shares: number;
  }>;
  yearly_returns: Array<{
    year: number;
    return_rate: number;
    stock_price_gain: number;
    dividend_gain: number;
    start_price: number;
    end_price: number;
    gross_dividends: number;
    dividend_tax: number;
    dividends: number;
  }>;
}

export default function StockAnalyzer() {
  const [symbol, setSymbol] = useState('');
  // 为每种投资模式创建独立的金额状态
  const [lumpSumAmount, setLumpSumAmount] = useState(''); // 全额投入金额
  const [dcaTotalBudget, setDcaTotalBudget] = useState(''); // 定额投入总预算
  const [hybridTotalBudget, setHybridTotalBudget] = useState(''); // 组合投入总预算
  
  const [expandedYears, setExpandedYears] = useState<Set<number>>(new Set());
  const [collapsingYears, setCollapsingYears] = useState<Set<number>>(new Set());
  const [years, setYears] = useState(1);
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [investmentMode, setInvestmentMode] = useState('lump_sum');
  const [isComposing, setIsComposing] = useState(false);

  const [loading, setLoading] = useState(false);
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [keyboardAnimating, setKeyboardAnimating] = useState(false);
  const [keyboardClosedTime, setKeyboardClosedTime] = useState(0);
  const [isKeyboardClosing, setIsKeyboardClosing] = useState(false);

  const [showResult, setShowResult] = useState(false);
  const [resultData, setResultData] = useState<AnalysisResult | null>(null);
  const [cardBottomHeight, setCardBottomHeight] = useState(75);
  const [showEasterEgg, setShowEasterEgg] = useState(false);
  
  // 定额投入和组合投入的额外参数
  const [investmentPeriod, setInvestmentPeriod] = useState('weekly'); // 'weekly' 或 'monthly'
  const [investmentDay, setInvestmentDay] = useState('周一'); // 周几或几号
  const [fixedAmount, setFixedAmount] = useState(''); // 定额投入金额
  const [initialAmount, setInitialAmount] = useState(''); // 组合投入的初始金额
  const [currentEditingField, setCurrentEditingField] = useState(''); // 当前正在编辑的字段
  const [showPeriodDayPicker, setShowPeriodDayPicker] = useState(false); // 控制周期日期选择器显示
  const [stockInputFocused, setStockInputFocused] = useState(false); // 股票输入框焦点状态
  const [activeTab, setActiveTab] = useState<'returns' | 'records'>('returns'); // Tab切换状态
  const [currentPage, setCurrentPage] = useState(1); // 购买记录当前页码
  const recordsPerPage = 10; // 每页显示的记录数
  
  // 触摸滑动相关状态
  const [touchStart, setTouchStart] = useState<{x: number, y: number} | null>(null);
  const [touchEnd, setTouchEnd] = useState<{x: number, y: number} | null>(null);
  const [isSwipeEnabled, setIsSwipeEnabled] = useState(false); // 控制是否启用滑动
  const [isDragging, setIsDragging] = useState(false); // 是否正在拖拽
  const [dragOffset, setDragOffset] = useState(0); // 拖拽偏移量
  const [swipeDirection, setSwipeDirection] = useState<'horizontal' | 'vertical' | null>(null); // 滑动方向
  const [isCalculating, setIsCalculating] = useState(false); // 防止重复计算

  // 防抖函数
  const debounce = useCallback((func: Function, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(null, args), delay);
    };
  }, []);

  // 监听activeTab变化，自动设置滑动状态
  useEffect(() => {
    if (activeTab === 'records' && shouldShowTabs()) {
      setIsSwipeEnabled(true);
    } else {
      setIsSwipeEnabled(false);
    }
  }, [activeTab]);

  // 获取当前投资模式对应的金额
  const getCurrentAmount = () => {
    switch (investmentMode) {
      case 'lump_sum':
        return lumpSumAmount;
      case 'dca':
        return dcaTotalBudget;
      case 'hybrid':
        return hybridTotalBudget;
      default:
        return '';
    }
  };

  // 设置当前投资模式对应的金额
  const setCurrentAmount = (value: string) => {
    switch (investmentMode) {
      case 'lump_sum':
        setLumpSumAmount(value);
        break;
      case 'dca':
        setDcaTotalBudget(value);
        break;
      case 'hybrid':
        setHybridTotalBudget(value);
        break;
    }
  };

  // 获取当前金额的显示格式
  const getDisplayAmount = () => {
    const currentAmount = getCurrentAmount();
    return currentAmount ? new Intl.NumberFormat('en-US').format(Number(currentAmount)) : '';
  };

  // 获取投资模式显示文字
  const getInvestmentModeText = () => {
    switch (investmentMode) {
      case 'lump_sum':
        return '全额投资';
      case 'dca':
        return '定额投资';
      case 'hybrid':
        return '组合投资';
      default:
        return '投资';
    }
  };

  // 判断是否需要显示Tab切换
  const shouldShowTabs = () => {
    return investmentMode === 'dca' || investmentMode === 'hybrid';
  };

  // 切换tab时重置分页
  const handleTabChange = (tab: 'returns' | 'records') => {
    setActiveTab(tab);
    if (tab === 'records') {
      setCurrentPage(1);
      setIsSwipeEnabled(true); // 切换到购买记录时启用滑动
    } else {
      setIsSwipeEnabled(false); // 切换到年回报时禁用滑动
    }
  };
  
  // 触摸开始事件
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isSwipeEnabled) return;
    setTouchEnd(null);
    setTouchStart({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    });
    setSwipeDirection(null);
    setDragOffset(0);
  };
  
  // 触摸移动事件
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isSwipeEnabled || !touchStart) return;
    
    const currentTouch = {
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    };
    
    // 如果还没有确定滑动方向，先判断方向
    if (swipeDirection === null) {
      const deltaX = Math.abs(currentTouch.x - touchStart.x);
      const deltaY = Math.abs(currentTouch.y - touchStart.y);
      
      // 只有在移动距离超过阈值时才判断方向
      if (deltaX > 15 || deltaY > 15) {
        // 计算滑动角度，确保手势足够接近水平或垂直
        const angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);
        const absAngle = Math.abs(angle);
        
        // 水平滑动：角度在-5到5度或175到180度之间
        if ((absAngle <= 5) || (absAngle >= 175)) {
          setSwipeDirection('horizontal');
          setIsDragging(true);
        }
        // 垂直滑动：角度在88到92度之间（接近90度）
        else if (absAngle >= 88 && absAngle <= 92) {
          setSwipeDirection('vertical');
          // 垂直滑动时不阻止默认行为，允许页面滚动
          return;
        }
        // 斜向滑动：不处理，避免混乱
        else {
          return;
        }
      } else {
        return; // 移动距离太小，不处理
      }
    }
    
    // 只处理水平滑动
    if (swipeDirection === 'horizontal') {
      e.preventDefault(); // 阻止垂直滚动
      
      const diff = currentTouch.x - touchStart.x;
      const totalPages = Math.ceil((resultData?.purchase_records?.length || 0) / recordsPerPage);
      
      // 限制拖拽范围，防止超出边界
      let limitedOffset = diff;
      if (currentPage === 1 && diff > 0) {
        limitedOffset = diff * 0.3; // 第一页右滑阻尼
      } else if (currentPage === totalPages && diff < 0) {
        limitedOffset = diff * 0.3; // 最后一页左滑阻尼
      }
      
      setDragOffset(limitedOffset);
    }
    
    setTouchEnd(currentTouch);
  };
  
  // 触摸结束事件
  const handleTouchEnd = () => {
    if (!isSwipeEnabled || !touchStart || !touchEnd) {
      setIsDragging(false);
      setDragOffset(0);
      setSwipeDirection(null);
      return;
    }
    
    // 只处理水平滑动的翻页逻辑
    if (swipeDirection === 'horizontal') {
      const distance = touchStart.x - touchEnd.x;
      const isLeftSwipe = distance > 50;
      const isRightSwipe = distance < -50;
      
      if (isLeftSwipe) {
        // 左滑 - 下一页
        handleNextPage();
      } else if (isRightSwipe) {
        // 右滑 - 上一页
        handlePrevPage();
      }
    }
    
    // 重置拖拽状态
    setIsDragging(false);
    setDragOffset(0);
    setSwipeDirection(null);
  };
  
  // 上一页
  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };
  
  // 下一页
  const handleNextPage = () => {
    const totalPages = Math.ceil((resultData?.purchase_records?.length || 0) / recordsPerPage);
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };
  
  // 获取分页数据
  const getPaginatedRecords = () => {
    if (!resultData?.purchase_records) return [];
    
    const totalPages = Math.ceil(resultData.purchase_records.length / recordsPerPage);
    const pages = [];
    
    for (let i = 1; i <= totalPages; i++) {
      const startIndex = (i - 1) * recordsPerPage;
      const endIndex = startIndex + recordsPerPage;
      const pageRecords = resultData.purchase_records.slice(startIndex, endIndex);
      pages.push(pageRecords);
    }
    
    return pages;
  };
  
  // 获取滑动容器的transform值
  const getSwipeTransform = () => {
    const baseTranslateX = -(currentPage - 1) * 100;
    
    if (isDragging && dragOffset !== 0 && swipeDirection === 'horizontal') {
      // 拖拽时的实时偏移，转换为百分比
      const containerWidth = window.innerWidth; // 使用视窗宽度作为参考
      const dragPercentage = (dragOffset / containerWidth) * 100;
      return `translateX(${baseTranslateX + dragPercentage}%)`;
    }
    
    return `translateX(${baseTranslateX}%)`;
  };

  const handleCalculate = async () => {
    // 防止重复计算
    if (isCalculating) {
      return;
    }
    
    // 防止键盘关闭后立即触发计算按钮（点击穿透问题）
    if (keyboardClosedTime && Date.now() - keyboardClosedTime < 500) {
      return; // 键盘关闭后500ms内不响应计算按钮点击
    }
    
    setIsCalculating(true);
    
    // 清除之前的toast
    toast.dismiss();
    
    if (!symbol) {
      toast.error('请输入股票代码');
      return;
    }

    // 根据投资模式验证不同的参数
    if (investmentMode === 'lump_sum') {
      if (!lumpSumAmount) {
        toast.error('请输入投资金额');
        return;
      }
    } else if (investmentMode === 'dca') {
      if (!fixedAmount) {
        toast.error('请输入定额投入金额');
        return;
      }
      if (!dcaTotalBudget) {
        toast.error('请输入总预算金额');
        return;
      }
      // 验证定额投入金额不能大于总预算
      const fixedAmountValue = parseFloat(fixedAmount);
      const totalBudgetValue = parseFloat(dcaTotalBudget);
      if (fixedAmountValue > totalBudgetValue) {
        toast.error('不能大于总投资金额哦');
        return;
      }
    } else if (investmentMode === 'hybrid') {
      if (!initialAmount) {
        toast.error('请输入初始投入金额');
        return;
      }
      if (!fixedAmount) {
        toast.error('请输入定额投入金额');
        return;
      }
      if (!hybridTotalBudget) {
        toast.error('请输入总预算金额');
        return;
      }
      // 验证组合投入的金额限制
      const initialAmountValue = parseFloat(initialAmount);
      const fixedAmountValue = parseFloat(fixedAmount);
      const totalBudgetValue = parseFloat(hybridTotalBudget);
      const remainingBudget = totalBudgetValue - initialAmountValue;
      
      if (initialAmountValue > totalBudgetValue) {
        toast.error('初始投入金额不能大于总投资金额哦');
        return;
      }
      if (fixedAmountValue > remainingBudget) {
        toast.error('不能大于总投资金额哦');
        return;
      }
    }

    setLoading(true);

    try {
      // 动态获取API地址，支持生产环境部署
      const getApiUrl = () => {
        const hostname = window.location.hostname;
        
        // 开发环境判断：本地和局域网都使用5001端口
        if (hostname === 'localhost' || 
            hostname === '127.0.0.1' ||
            hostname.startsWith('192.168.') ||
            hostname.startsWith('10.') ||
            hostname.match(/^172\.(1[6-9]|2[0-9]|3[0-1])\./)) {
          // 开发环境：直连后端端口
          return `http://${hostname}:5001`;
        } 
        // 生产环境：通过nginx反向代理访问（如：170.106.170.137）
        else {
          return `${window.location.protocol}//${window.location.host}`;
        }
      };

      // 构建请求数据
      const requestData: any = {
        symbol: symbol.toUpperCase(),
        investment_mode: investmentMode === 'dca' ? 'fixed' : investmentMode === 'hybrid' ? 'combined' : investmentMode,
        years: years,
        start_date: startDate || undefined
      };

      // 根据投资模式添加不同的参数
      if (investmentMode === 'lump_sum') {
        requestData.amount = parseFloat(lumpSumAmount);
      } else if (investmentMode === 'dca') {
        requestData.total_budget = parseFloat(dcaTotalBudget);
        requestData.fixed_amount = parseFloat(fixedAmount);
        requestData.investment_period = investmentPeriod;
        requestData.investment_day = investmentDay;
      } else if (investmentMode === 'hybrid') {
        requestData.total_budget = parseFloat(hybridTotalBudget);
        requestData.initial_amount = parseFloat(initialAmount);
        requestData.remaining_budget = parseFloat(hybridTotalBudget) - parseFloat(initialAmount);
        requestData.fixed_amount = parseFloat(fixedAmount);
        requestData.investment_period = investmentPeriod;
        requestData.investment_day = investmentDay;
      }

      const response = await axios({
        method: 'POST',
        url: `${getApiUrl()}/api/analyze`,
        data: requestData,
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 30000, // 30秒超时
      });

      // 检查是否有成交（金额为0或股数为0表示没有成交）
      if (!response.data.shares_bought || response.data.shares_bought === 0 || 
          !response.data.investment_amount || response.data.investment_amount === 0) {
        toast.error('金额没有成交，调整下金额哦');
        return;
      }

      // 设置结果数据
      setResultData(response.data);
      
      // 检查是否触发彩蛋
      if (response.data.total_dividends && checkEasterEgg(response.data.total_dividends)) {
        setShowEasterEgg(true);
      } else {
        setShowResult(true);
      }
    } catch (error: any) {
      let errorMessage = '分析失败，请重试';
      
      if (error.response) {
        // 处理405错误（方法不允许）
        if (error.response.status === 405) {
          errorMessage = '请求方法错误，请刷新页面后重试';
        } else {
          errorMessage = error.response.data?.error || `请求失败 (${error.response.status})`;
        }
      } else if (error.request) {
        errorMessage = '网络连接失败，请检查网络后重试';
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      // 延迟重置计算状态，防止过快重复点击
      setTimeout(() => {
        setIsCalculating(false);
      }, 1000);
    }
  };

  // 创建防抖版本的计算函数
  const debouncedCalculate = useCallback(
    debounce(handleCalculate, 300),
    [symbol, lumpSumAmount, dcaTotalBudget, fixedAmount, hybridTotalBudget, initialAmount, years, startDate, investmentMode, investmentPeriod, investmentDay, isCalculating, keyboardClosedTime]
  );

  const handleBackToInput = () => {
    setShowResult(false);
    setShowEasterEgg(false);
    // 延迟清空resultData，等待动画完成
    setTimeout(() => {
      setResultData(null);
    }, 500); // 与CSS动画时长一致
  };

  const toggleYearExpansion = (year: number) => {
    setExpandedYears(prev => {
      // 手风琴模式：如果点击的年份已经展开，则收起；否则只展开这一个年份
      if (prev.has(year)) {
        // 收起当前展开的年份
        setCollapsingYears(new Set([year]));
        setTimeout(() => {
          setCollapsingYears(new Set());
        }, 300); // 动画持续时间 - 必须与CSS中的动画时间保持一致(0.3s = 300ms)
                 // 调整动画速度：修改这里的300可以改变收起动画的等待时间
                 // 数值越小动画越快，数值越大动画越慢，例如：200(快) 500(慢)
                 // 注意：这个值必须与CSS中.year-details-expanded和.collapsing的动画时间匹配
        return new Set();
      } else {
        // 先标记其他年份为收起状态
        const currentExpanded = Array.from(prev);
        if (currentExpanded.length > 0) {
          setCollapsingYears(new Set(currentExpanded));
          setTimeout(() => {
            setCollapsingYears(new Set());
          }, 300); // 动画持续时间 - 与上面的时间保持一致
        }
        // 只展开点击的年份
        return new Set([year]);
      }
    });
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(e.target.value, 10);
    // 保证滑块的最小值是1
    if (newValue < 1) {
      setYears(1);
      return;
    }
    setYears(newValue);
  };

  const onKeyPress = (button: string) => {
    if (button === '{bksp}') {
      if (currentEditingField === 'amount') {
        const currentAmount = getCurrentAmount();
        const newAmount = currentAmount.slice(0, -1);
        setCurrentAmount(newAmount);
      } else if (currentEditingField === 'fixedAmount') {
        setFixedAmount(prev => prev.slice(0, -1));
      } else if (currentEditingField === 'initialAmount') {
        setInitialAmount(prev => prev.slice(0, -1));
      }

    } else if (button === '{enter}') {
      // 在确认前进行金额验证
      let validationError = '';
      
      if (currentEditingField === 'fixedAmount') {
        const fixedAmountValue = parseFloat(fixedAmount);
        
        if (investmentMode === 'dca') {
          const totalBudget = parseFloat(dcaTotalBudget);
          if (totalBudget && fixedAmountValue > totalBudget) {
            validationError = '不能大于总投资金额哦';
          }
        } else if (investmentMode === 'hybrid') {
          const totalBudget = parseFloat(hybridTotalBudget);
          const initialAmountValue = parseFloat(initialAmount) || 0;
          const remainingBudget = totalBudget - initialAmountValue;
          if (totalBudget && fixedAmountValue > remainingBudget) {
            validationError = '不能大于总投资金额哦';
          }
        }
      } else if (currentEditingField === 'initialAmount' && investmentMode === 'hybrid') {
        const initialAmountValue = parseFloat(initialAmount);
        const totalBudget = parseFloat(hybridTotalBudget);
        if (totalBudget && initialAmountValue > totalBudget) {
          validationError = '不能大于总投资金额哦';
        }
      }
      
      if (validationError) {
        toast.dismiss();
        toast.error(validationError);
        // 不关闭键盘，保持在输入状态
        return;
      }
      
      setKeyboardAnimating(false);
      setIsKeyboardClosing(true); // 标记键盘正在关闭
      setTimeout(() => {
        setShowKeyboard(false);
        setCurrentEditingField(''); // 重置编辑字段状态，恢复占位符显示
        setKeyboardClosedTime(Date.now()); // 记录键盘关闭时间
        
        // 防止点击穿透：额外延迟200ms确保键盘完全消失后才允许其他交互
        setTimeout(() => {
          setIsKeyboardClosing(false); // 键盘完全关闭，重置状态
        }, 200);
      }, 300);
    } else if (/^[0-9]$/.test(button)) {
      const getCurrentValue = () => {
        if (currentEditingField === 'amount') return getCurrentAmount();
        if (currentEditingField === 'fixedAmount') return fixedAmount;
        if (currentEditingField === 'initialAmount') return initialAmount;
        return '';
      };
      
      const currentValue = getCurrentValue();
      
      // 如果当前金额为空且按下的是0，则不允许输入
      if (currentValue === '' && button === '0') {
        return;
      }
      
      // 检查是否超过7位数字，如果是则显示toast提示
      if (currentValue.length >= 7) {
        toast.dismiss();
        toast.error('目前金额只支持7位数');
        return;
      }
      
      const newValue = currentValue + button;
      
      if (currentEditingField === 'amount') {
        setCurrentAmount(newValue);
      } else if (currentEditingField === 'fixedAmount') {
        setFixedAmount(newValue);
      } else if (currentEditingField === 'initialAmount') {
        setInitialAmount(newValue);
      }
    }
  };

  const onAmountFocus = () => {
    // 关闭股票代码输入框的焦点
    const stockInput = document.querySelector('.stock-input') as HTMLInputElement;
    if (stockInput) {
      stockInput.blur();
    }
    
    // 关闭周期选择器
    if (showPeriodDayPicker) {
      setShowPeriodDayPicker(false);
    }
    
    setCurrentEditingField('amount');
    setShowKeyboard(true);
    setTimeout(() => setKeyboardAnimating(true), 10);
  };
  
  const onFixedAmountFocus = () => {
    // 关闭股票代码输入框的焦点
    const stockInput = document.querySelector('.stock-input') as HTMLInputElement;
    if (stockInput) {
      stockInput.blur();
    }
    
    // 关闭周期选择器
    if (showPeriodDayPicker) {
      setShowPeriodDayPicker(false);
    }
    
    setCurrentEditingField('fixedAmount');
    setShowKeyboard(true);
    setTimeout(() => setKeyboardAnimating(true), 10);
  };
  
  const onInitialAmountFocus = () => {
    // 关闭股票代码输入框的焦点
    const stockInput = document.querySelector('.stock-input') as HTMLInputElement;
    if (stockInput) {
      stockInput.blur();
    }
    
    // 关闭周期选择器
    if (showPeriodDayPicker) {
      setShowPeriodDayPicker(false);
    }
    
    setCurrentEditingField('initialAmount');
    setShowKeyboard(true);
    setTimeout(() => setKeyboardAnimating(true), 10);
  };

  const onStockInputFocus = () => {
    // 关闭键盘并重置编辑字段
    if (showKeyboard) {
      setKeyboardAnimating(false);
      setTimeout(() => setShowKeyboard(false), 300);
    }
    setCurrentEditingField('');
    setStockInputFocused(true);
  };

  const onStockInputBlur = () => {
    setStockInputFocused(false);
  };



  // 组件挂载或years变化时，更新滑块填充样式
  useEffect(() => {
    const slider = document.querySelector('.years-slider') as HTMLInputElement;
    if (slider) {
      // 百分比计算：(当前值 / 最大值) * 100
      const percentage = (years / 5) * 100;
      slider.style.background = `linear-gradient(to right, #466AEA 0%, #466AEA ${percentage}%, rgba(70, 106, 234, 0.1) ${percentage}%, rgba(70, 106, 234, 0.1) 100%)`;
    }
  }, [years]);

  // 根据当前显示内容动态计算下半部分高度
  useEffect(() => {
    if (resultData) {
      const baseHeight = 55; // 基础高度，包含标题和说明文字
      const tabHeight = shouldShowTabs() ? 60 : 0; // Tab切换区域高度
      let contentHeight = 0;
      
      if (!shouldShowTabs() || activeTab === 'returns') {
        // 年度回报内容高度计算
        if (resultData.yearly_returns) {
          const itemHeight = 80; // 每个年度项目的基础高度（包含20px间距）
          const expandedItemHeight = 330; // 展开状态下的额外高度
          
          resultData.yearly_returns.forEach((item) => {
            contentHeight += itemHeight;
            if (expandedYears.has(item.year)) {
              contentHeight += expandedItemHeight;
            }
          });
          
          if (resultData.yearly_returns.length > 0) {
            contentHeight -= 20; // 减去最后一个元素的20px
          }
        }
      } else if (activeTab === 'records') {
        // 购买记录内容高度计算 - 根据是否有翻页决定高度策略
        if (resultData.purchase_records && resultData.purchase_records.length > 0) {
          const recordsPerPage = 10;
          const hasPagination = resultData.purchase_records.length > recordsPerPage;
          
          if (hasPagination) {
            // 有翻页时使用固定高度，确保所有页面（包括最后一页）高度一致
            const headerHeight = 40;
            const paginationHeight = 50;
            contentHeight = headerHeight + (recordsPerPage * 40) + paginationHeight;
          } else {
            // 没有翻页时使用自适应高度
            const headerHeight = 40;
            const rowHeight = 40;
            const actualRecords = resultData.purchase_records.length;
            contentHeight = headerHeight + (actualRecords * rowHeight) + 20; // 加20px底部间距
          }
        } else {
          contentHeight = 60; // 无数据时的最小高度
        }
      }
      
      const totalHeight = baseHeight + tabHeight + contentHeight;
      const calculatedHeight = Math.max(75, totalHeight);
      setCardBottomHeight(calculatedHeight);
    }
  }, [resultData, expandedYears, activeTab, currentPage]);

  const investmentModes = [
    { key: 'lump_sum', label: '全额投入' },
    { key: 'dca', label: '定额投入' },
    { key: 'hybrid', label: '组合投入' }
  ];

  // 获取当前选中模式的索引，用于滑动动效
  const getModeIndex = () => {
    return investmentModes.findIndex(mode => mode.key === investmentMode);
  };

  // 处理投资模式切换，添加滑动动效
  const handleModeChange = (modeKey: string) => {
    setInvestmentMode(modeKey);
    
    // 更新CSS类来触发滑动动效
    const selector = document.querySelector('.mode-selector');
    if (selector) {
      // 移除所有模式类
      selector.classList.remove('mode-0', 'mode-1', 'mode-2');
      
      // 添加对应的模式类
      const modeIndex = investmentModes.findIndex(mode => mode.key === modeKey);
      selector.classList.add(`mode-${modeIndex}`);
    }
  };
  
  // 处理投资频率切换
  const handlePeriodChange = (period: string) => {
    setInvestmentPeriod(period);
    // 切换频率时重置投资日
    if (period === 'weekly') {
      setInvestmentDay('周一');
    } else {
      setInvestmentDay('1号');
    }
  };

  // 处理周期日期选择器变化
  const handlePeriodDayChange = (period: string, day: string) => {
    setInvestmentPeriod(period);
    setInvestmentDay(day);
  };

  // 打开周期日期选择器
  const handlePeriodDayPickerOpen = () => {
    // 关闭数字键盘
    if (showKeyboard) {
      setKeyboardAnimating(false);
      setTimeout(() => setShowKeyboard(false), 300);
      setCurrentEditingField('');
    }
    setShowPeriodDayPicker(true);
  };

  // 关闭周期日期选择器
  const handlePeriodDayPickerClose = () => {
    setShowPeriodDayPicker(false);
  };

  // 获取显示的周期文本
  const getPeriodDisplayText = () => {
    const periodMapping: { [key: string]: string } = {
      'daily': '每天',
      'weekly': '每周',
      'biweekly': '每两周',
      'monthly': '每月'
    };
    return periodMapping[investmentPeriod] || '每周';
  };

  // 获取显示的日期文本
  const getDayDisplayText = () => {
    if (investmentPeriod === 'daily' || !investmentDay) {
      return '';
    }
    return investmentDay;
  };
  
  // 获取投资日选项
  const getInvestmentDayOptions = () => {
    if (investmentPeriod === 'weekly') {
      return ['周一', '周二', '周三', '周四', '周五'];
    } else {
      return ['1号', '2号', '3号', '4号', '5号', '6号', '7号', '8号', '9号', '10号',
              '11号', '12号', '13号', '14号', '15号', '16号', '17号', '18号', '19号', '20号',
              '21号', '22号', '23号', '24号', '25号', '26号', '27号', '28号'];
    }
  };

  // 处理日期输入变化
  const handleDateChange = (date: Date) => {
    // 使用本地时间格式化，避免时区转换问题
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    setStartDate(`${year}-${month}-${day}`);
  };
  
  const handleDatePickerOpen = () => {
    // 关闭数字键盘
    if (showKeyboard) {
      setKeyboardAnimating(false);
      setTimeout(() => setShowKeyboard(false), 300);
      setCurrentEditingField('');
    }
    
    // 如果键盘刚刚关闭（500ms内），则延迟打开日期选择器
    if (Date.now() - keyboardClosedTime < 500) {
      setTimeout(() => setShowDatePicker(true), 100);
    } else {
      setShowDatePicker(true);
    }
  };
  
  const handleDatePickerClose = () => {
    setShowDatePicker(false);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatPercentage = (rate: number) => {
    const sign = rate >= 0 ? '+' : '';
    return `${sign}${rate.toFixed(1)}%`;
  };

  // 根据数字位数获取字号调整类名
  const getFontSizeClass = (value: number, threshold1: number, threshold2?: number, formatAsDecimal?: boolean) => {
    // 如果是分红字段，使用toFixed(1)格式化后的字符串来计算位数
    const valueStr = formatAsDecimal ? Math.abs(value).toFixed(1) : Math.abs(value).toString();
    // 计算所有数字位数（包括小数点后的0，因为0-9都算数字）
    const digitCount = valueStr.replace(/[^0-9]/g, '').length;
    
    if (threshold2 && digitCount >= threshold2) {
      return 'extra-large-number';
    } else if (digitCount >= threshold1) {
      return 'large-number';
    }
    return '';
  };

  // 检查是否触发彩蛋（分红达到8位数字）
  const checkEasterEgg = (dividendValue: number) => {
    const dividendStr = Math.abs(dividendValue).toString();
    const digitCount = dividendStr.replace('.', '').length;
    return digitCount >= 9;
  };

  // 自动适应字号统一处理数量和分红字段的字号，确保一致性
  const getUnifiedFontSizeClass = (quantityValue: number, dividendValue: number) => {
    const quantityClass = getFontSizeClass(Math.round(quantityValue || 0), 5, 6);
    // 分红字段使用formatAsDecimal=true，基于toFixed(1)格式化后的字符串计算位数
    const dividendClass = getFontSizeClass(dividendValue || 0, 5, 6, true);
    
    // 如果任一字段需要缩小，则两个字段都使用相同的缩小级别
    if (quantityClass === 'extra-large-number' || dividendClass === 'extra-large-number') {
      return 'extra-large-number';
    } else if (quantityClass === 'large-number' || dividendClass === 'large-number') {
      return 'large-number';
    }
    return '';
  };

  return (
    <div className={`stock-analyzer ${showResult ? 'show-result' : ''}`}>
      {/* 彩蛋页面 */}
      {showEasterEgg && (
        <EasterEggPage onBackToInput={handleBackToInput} />
      )}
      {/* 输入页面 */}
      <div className={`input-page ${showResult ? 'slide-up' : ''}`}>
      <Toaster 
         position="top-center" // Toast显示位置：top-center, top-left, top-right, bottom-center等
         containerStyle={{
           // Toast容器的位置样式 - 可以调整这些值来改变Toast的位置
           top: '55%',  // 距离顶部的位置，可以改为其他值如 '20%', '60%', '100px' 等
           transform: 'translateY(-50%)'  // 垂直居中偏移，可以改为 'translateY(0)' 或其他值
         }}
         toastOptions={{
           duration: 1700, // Toast显示时长（毫秒）- 延迟0.2秒
           style: {
             // Toast的外观样式
             background: 'rgba(0, 0, 0, 0.8)', // 背景色
             color: '#FFFFFF', // 文字颜色
             borderRadius: '10px', // 圆角
             padding: '16px 16px', // 内边距：上下20px，左右16px
             fontSize: '16px', // 字体大小
             fontFamily: 'PingFang SC, -apple-system, BlinkMacSystemFont, sans-serif', // 字体
             fontWeight: '400', // 字重
             textAlign: 'center', // 文字对齐
             border: 'none', // 边框
             boxShadow: 'none', // 阴影
             display: 'flex', // 使用flex布局
             flexDirection: 'column', // 垂直排列（图标在上，文字在下）
             alignItems: 'center', // 水平居中
             justifyContent: 'center', // 垂直居中
             gap: '8px', // 图标和文字之间的间距
             minHeight: '80px', // 最小高度确保有足够空间居中
             minWidth: '120px' // 最小宽度
           },
           success: {
             iconTheme: {
               primary: '#FFFFFF', // 成功图标主色
               secondary: 'rgba(0, 0, 0, 0.8)' // 成功图标次色
             }
           },
           error: {
             iconTheme: {
               primary: '#FFFFFF',  // 错误图标主色
               secondary: 'rgba(0, 0, 0, 0.8)' // 错误图标次色
             }
           }
         }}
       />
      {/* 蓝色头部区域 */}
      <div className="header-section">
        <div className="header-content">
          <h1 className="main-title">美股评估助手</h1>
          <p className="subtitle">"轻松查看历史收益，优化投资策略"</p>
        </div>
        
        {/* 🔴【红框1 - 股票代码输入框】跨越蓝色头部和白色内容区域的分割线 */}
        {/* 📍位置调整：在CSS的.stock-input-section中修改bottom值来调整垂直位置 */}
        {/* 📍宽度调整：在CSS的.stock-input-section中修改left和right值来调整左右间距 */}
        <div className="stock-input-section">
          <div className="input-container">
            {/* 🔴【股票代码输入框样式设置】 */}
            {/* 📝字体大小：在CSS的.stock-input中修改font-size来调整字体大小（当前20px） */}
            {/* 📝占位符字体：在CSS的.stock-input::placeholder中修改font-size来调整占位符字体大小（当前20px） */}
            {/* 📝文字对齐：在CSS的.stock-input中修改text-align来调整文字对齐方式（当前center居中） */}
            {/* 📝输入框高度：在CSS的.input-container中修改height来调整输入框高度（当前60px） */}
            {/* 📝圆角大小：在CSS的.input-container中修改border-radius来调整圆角（当前10px） */}
            <input
              type="text"
              value={symbol}
              onChange={(e) => {
                // 如果正在输入法组合中，不进行过滤
                if (isComposing) {
                  setSymbol(e.target.value);
                } else {
                  // 只允许输入英文字母，自动转换为大写
                  const value = e.target.value.replace(/[^a-zA-Z]/g, '').toUpperCase();
                  setSymbol(value);
                }
              }}
              onCompositionStart={() => setIsComposing(true)}
              onCompositionEnd={(e) => {
                setIsComposing(false);
                // 输入法结束后，清理非英文字母并转换为大写
                const value = e.currentTarget.value.replace(/[^a-zA-Z]/g, '').toUpperCase();
                setSymbol(value);
              }}
              onFocus={onStockInputFocus}
              onBlur={onStockInputBlur}
              placeholder={(!stockInputFocused && !symbol) ? "股票代码，例如：AAPL、TSLA" : ""}
              className="stock-input"
            />
          </div>
        </div>
      </div>

      {/* 主要内容区域 */}
      <div className="main-content">
        {/* 🔴【红框2 - 投资模式选择】三个选项卡：全额投入、定投投入、组合投入 */}
        {/* 📍间距调整：在CSS的.investment-mode-section中修改margin-bottom来调整与下方元素的间距（当前20px） */}
        {/* 📝选项卡字体：在CSS的.mode-option中修改font-size来调整选项卡字体大小（当前16px） */}
        {/* 📝选项卡高度：在CSS的.mode-selector中修改height来调整选项卡容器高度（当前40px） */}
        {/* 📝圆角大小：在CSS的.mode-selector中修改border-radius来调整圆角（当前5px） */}
        <div className="investment-mode-section">
          <div className={`mode-selector mode-${getModeIndex()}`}>
            {investmentModes.map((mode, index) => (
              <div
                key={mode.key}
                className={`mode-option ${investmentMode === mode.key ? 'active' : ''}`}
                onClick={() => handleModeChange(mode.key)}
              >
                {mode.label}
              </div>
            ))}
          </div>
        </div>

        {/* 🔴【红框3 - 投资金额输入框】点击后弹出数字键盘 */}
        {/* 📍间距调整：在CSS的.input-section中修改margin-bottom来调整与下方元素的间距（当前20px） */}
        {/* 📝字体大小：在CSS的.amount-display中修改font-size来调整显示金额的字体大小（当前22px） */}
        {/* 📝占位符字体：在CSS的.amount-display .placeholder中修改font-size来调整占位符字体大小（当前20px） */}
        {/* 📝输入框高度：在CSS的.input-container中修改height来调整输入框高度（当前60px） */}
        {/* 📝圆角大小：在CSS的.input-container中修改border-radius来调整圆角（当前10px） */}
        <div className="input-section">
          <div className="input-container">
            <div className="amount-display" onClick={onAmountFocus}>
              {!getCurrentAmount() && currentEditingField !== 'amount' && (
                <span className="placeholder">
                  {investmentMode === 'lump_sum' ? '总投资金额' : 
                   investmentMode === 'dca' ? '总投资金额' : 
                   '总投资金额'}
                </span>
              )}
              {(getCurrentAmount() || (showKeyboard && currentEditingField === 'amount')) && (
                <>
                  <span className="amount-text">
                    {getDisplayAmount()}
                  </span>
                  {showKeyboard && currentEditingField === 'amount' && (
                    <span className="cursor-icon">
                      <CursorIcon />
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* 定额投入和组合投入的额外参数 */}
        {(investmentMode === 'dca' || investmentMode === 'hybrid') && (
          <>
            {/* 组合投入的初始金额 */}
            {investmentMode === 'hybrid' && (
              <div className="input-section">
                <div className="input-container">
                  <div className="amount-display" onClick={onInitialAmountFocus}>
                    {!initialAmount && currentEditingField !== 'initialAmount' && (
                      <span className="placeholder">初始投入金额</span>
                    )}
                    {(initialAmount || (showKeyboard && currentEditingField === 'initialAmount')) && (
                      <>
                        <span className="amount-text">
                          {initialAmount ? parseFloat(initialAmount).toLocaleString() : ''}
                        </span>
                        {showKeyboard && currentEditingField === 'initialAmount' && (
                          <span className="cursor-icon">
                            <CursorIcon />
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* 定额投入金额和周期日期选择合并模块 */}
            <div className="input-section">
              <div className="combined-input-container">
                {/* 周期日期选择 - 上半部分 */}
                <div className="period-day-container" onClick={handlePeriodDayPickerOpen}>
                  <div className="period-day-display">
                    <span className="period-text">{getPeriodDisplayText()}</span>
                    {getDayDisplayText() && (
                      <span className="day-text">{getDayDisplayText()}</span>
                    )}
                  </div>
                  <span className="dropdown-arrow">
                    <svg xmlns="http://www.w3.org/2000/svg" width="8" height="14" viewBox="0 0 8 14" fill="none">
                      <path d="M1.95586 0.183951C1.76856 -0.0189591 1.46543 -0.0189591 1.27812 0.183951L0.867968 0.627311C0.680954 0.830139 0.681006 1.15882 0.867968 1.36169L6.02519 6.94762L0.910937 12.4877C0.723651 12.6906 0.723684 13.0191 0.910937 13.222L1.32012 13.6664C1.50741 13.8692 1.81155 13.8692 1.99883 13.6664L7.84746 7.32946C8.01819 7.14432 8.03229 6.85518 7.89141 6.65173C7.86913 6.60446 7.841 6.55944 7.80449 6.51989L1.95586 0.183951Z" fill="#C9CDD4"/>
                    </svg>
                  </span>
                </div>
                
                {/* 定额投入金额 - 下半部分 */}
                <div className="amount-display" onClick={onFixedAmountFocus}>
                  {!fixedAmount && currentEditingField !== 'fixedAmount' && (
                    <span className="placeholder">
                      {investmentMode === 'dca' ? '每次投资金额' : '每次投资金额'}
                    </span>
                  )}
                  {(fixedAmount || (showKeyboard && currentEditingField === 'fixedAmount')) && (
                    <>
                      <span className="amount-text">
                        {fixedAmount ? parseFloat(fixedAmount).toLocaleString() : ''}
                      </span>
                      {showKeyboard && currentEditingField === 'fixedAmount' && (
                        <span className="cursor-icon">
                          <CursorIcon />
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* 🔴【红框4 - 投资年限滑块】拖动滑块选择1-5年 */}
        {/* 📍间距调整：在CSS的.years-section中修改margin-bottom来调整与下方元素的间距（当前30px） */}
        {/* 📝标签字体：在CSS的.years-label和.years-value中修改font-size来调整标签字体大小（当前15px） */}
        {/* 📝滑块高度：在CSS的.years-slider中修改height来调整滑块轨道高度（当前4px） */}
        {/* 📝滑块按钮：在CSS的.years-slider::-webkit-slider-thumb中修改width和height来调整滑块按钮大小（当前24px） */}
        {/* 📝滑块颜色：在CSS的.years-slider中修改background来调整滑块颜色 */}
        <div className="years-section">
          <div className="years-header">
            <span className="years-label">投资年限</span>
            <span className="years-value">{years}年</span>
          </div>
          <div className="slider-container">
            <input
              type="range"
              min="0"
              max="5"
              step="1"
              value={years}
              onChange={handleSliderChange}
              className="years-slider"
            />
          </div>
        </div>

        {/* 🔴【红框5 - 日期选择器】点击后弹出日期选择界面 */}
        {/* 📍间距调整：在CSS的.date-section中修改margin-bottom来调整与下方元素的间距（当前40px） */}
        {/* 📝日期字体：在CSS的.date-display中修改font-size来调整日期显示字体大小（当前20px） */}
        {/* 📝占位符字体：在CSS的.date-placeholder中修改font-size来调整占位符字体大小（当前20px） */}
        {/* 📝提示文字：在CSS的.date-hint中修改font-size来调整提示文字字体大小（当前15px） */}
        {/* 📝输入框高度：在CSS的.date-container中修改height来调整输入框高度（当前60px） */}
        {/* 📝圆角大小：在CSS的.date-container中修改border-radius来调整圆角（当前10px） */}
        <div className="date-section">
          <div className="date-container" onClick={handleDatePickerOpen}>
            {startDate && (
              <div className="date-display">
                {(() => {
                  const date = new Date(startDate);
                  const year = date.getFullYear();
                  const month = date.getMonth() + 1;
                  const day = date.getDate();
                  return `${year}.${month}.${day}`;
                })()}
              </div>
            )}
            {!startDate && (
              <div className="date-placeholder">年 / 月 / 日</div>
            )}
            <div className="calendar-icon">
              <CalendarIcon />
            </div>
          </div>
          <p className="date-hint">* 倒推回测年份，可选择指定日期</p>
        </div>

        {/* 🔴【红框6 - 开始计算按钮】点击后开始分析股票数据 */}
        {/* 📝按钮文字：在CSS的.button-text中修改font-size来调整按钮文字字体大小（当前18px） */}
        {/* 📝按钮高度：在CSS的.calculate-button中修改height来调整按钮高度（当前64px） */}
        {/* 📝按钮宽度：在CSS的.calculate-button中修改max-width来调整按钮最大宽度（当前280px） */}
        {/* 📝圆角大小：在CSS的.calculate-button中修改border-radius来调整圆角（当前36px） */}
        {/* 📝图标大小：直接在下方style中修改fontSize来调整箭头图标大小（当前16px） */}
        <div className="button-section">
          <button
            onClick={debouncedCalculate}
            disabled={loading || isKeyboardClosing || isCalculating}
            className="calculate-button"
          >
            <div className="button-circle">
              {/* 🔴【箭头图标大小调整】修改fontSize值来调整图标大小 */}
              <ArrowDownIcon style={{ fontSize: '16px' }} />
            </div>
            <span className="button-text">
              {loading ? '计算中...' : '开始计算'}
            </span>
          </button>
        </div>
        
        {/* Logo - 页面底部 */}
        <div className="bottom-logo">
          <LogoIcon />
        </div>
        
        {/* 底部信息文字 */}
        <div className="bottom-info-text">
          <div className="info-line-1">
            系统由AI全程参与，模型：claude-4、gemni-2.5-pro、GPT4o
          </div>
          <div className="info-line-2">
            数据源来:tiingo、yfinance、FMP     系统：半监督机器学习
          </div>
        </div>

      </div>
      
      {showKeyboard && (
        <div className={`keyboard-container ${keyboardAnimating ? 'show' : 'hide'}`}>
          <Keyboard
             onKeyPress={onKeyPress}
             layout={{
               default: [
                 "1 2 3",
                 "4 5 6",
                 "7 8 9",
                 "0 {bksp} {enter}"
               ]
             }}
             display={{
               '{bksp}': '⌫',
               '{enter}': '确定'
             }}
             theme="hg-theme-default custom-numeric-keyboard"
             buttonTheme={[
               {
                 class: "delete-button",
                 buttons: "{bksp}"
               },
               {
                 class: "confirm-button",
                 buttons: "{enter}"
               },
               {
                 class: getCurrentAmount() === '' ? "zero-button-disabled" : "zero-button-enabled",
                 buttons: "0"
               }
             ]}
           />
        </div>
      )}
      
      {/* 自定义日期选择器 */}
      <MobileDatePicker
        value={new Date(startDate)}
        onChange={handleDateChange}
        isOpen={showDatePicker}
        onClose={handleDatePickerClose}
      />
      
      {/* 周期日期选择器 */}
      <PeriodDayPicker
        period={investmentPeriod}
        day={investmentDay}
        isOpen={showPeriodDayPicker}
        onChange={handlePeriodDayChange}
        onClose={handlePeriodDayPickerClose}
      />
      </div>
      
      {/* 结果页面 */}
      <div className={`result-page ${showResult ? 'slide-in' : ''}`}>
        {resultData && (
          <>
            {/* 股票符号和投资标签放在同一行 */}
            <div className="header-container">
              <div className="stock-symbol-display">
                {resultData.stock_symbol}
              </div>
              
              <div className="investment-tag-display">
                {getInvestmentModeText()}：$ {(resultData.investment_amount || 0).toLocaleString()}
              </div>
            </div>
            
            {/* 投资日期范围 - 单独一行在股票代码下方 */}
            <div className="investment-date-range">
              {resultData.purchase_date} ~ {resultData.current_date}
            </div>
            
            {/* 指标标签 */}
            <div className="metrics-labels">
              <div className="metric-label-left">最终资产</div>
              <div className="metric-label-right">总回报率</div>
            </div>
            
            {/* 指标数值 */}
            <div className="metrics-values">
              <div className={`final-amount ${getFontSizeClass(resultData.current_stock_value || 0, 8, 10)}`}>
                {formatCurrency(resultData.current_stock_value || 0)}
              </div>
              <div className={`total-return ${(resultData.total_return_rate || 0) >= 0 ? 'positive' : 'negative'}`}>{formatPercentage(resultData.total_return_rate || 0)}</div>
            </div>
            
            {/* 白色卡片区域 - 分为上下两部分 */}
            <div className="white-card-container">
              {/* 固定的上半部分 */}
              <div className="card-top-section">
                <CardTopSvg />
                <div className="card-top-content">
              {/* 股票详情 - 三列布局 */}
              <div className="stock-details-grid">
                <div className="detail-column">
                  <div className="detail-label">当前价 / 购入价($)</div>
                  <div className="detail-value">
                    <div className={`current-price ${getFontSizeClass(resultData.current_price || 0, 6, 8)} ${
                      resultData.current_price > resultData.purchase_price ? 'price-up' :
                      resultData.current_price < resultData.purchase_price ? 'price-down' : 'price-neutral'
                    }`}>
                      <span>{(resultData.current_price || 0).toFixed(2)}</span>
                      {resultData.current_price > resultData.purchase_price ? (
                        <span className="price-arrow-up">↑</span>
                      ) : resultData.current_price < resultData.purchase_price ? (
                        <span className="price-arrow-down">↓</span>
                      ) : null}
                    </div>
                    <div className="purchase-price">
                      {(resultData.purchase_price || 0).toFixed(2)}
                    </div>
                  </div>
                </div>
                
                <div className="detail-column">
                  <div className="detail-label">数量</div>
                  <div className={`detail-value quantity ${getUnifiedFontSizeClass(resultData.shares_bought || 0, resultData.total_dividends || 0)}`}>
                    {Math.round(resultData.shares_bought || 0)}
                  </div>
                </div>
                
                <div className="detail-column">
                  <div className="detail-label">总分红($)</div>
                  <div className={`detail-value dividend ${getUnifiedFontSizeClass(resultData.shares_bought || 0, resultData.total_dividends || 0)}`}>
                    {(resultData.total_dividends || 0).toFixed(1)}
                  </div>
                </div>
              </div>
                </div>
              </div>
              
              {/* 可拉长的下半部分 */}
              <div className="card-bottom-section">
                <CardBottomSvg height={cardBottomHeight} />
                <div className="card-bottom-content">
                  {/* 年回报详情分隔线 - 固定显示，不受其他组件影响 */}
                  <div className="returns-title">
                    <span className="returns-dash">------------</span>
                    <span className="returns-text"> 年回报详情 </span>
                    <span className="returns-dash">------------</span>
                  </div>
                  
                  {/* Tab切换区域 - 仅在定额投资和组合投资模式下显示 */}
                  {shouldShowTabs() && (
                    <div className="tab-section">
                      <div className="tab-buttons">
                        <button 
                          className={`tab-button ${activeTab === 'returns' ? 'active' : ''}`}
                          onClick={() => handleTabChange('returns')}
                        >
                          年回报
                        </button>
                        <button 
                          className={`tab-button ${activeTab === 'records' ? 'active' : ''}`}
                          onClick={() => handleTabChange('records')}
                        >
                          购买记录
                        </button>
                      </div>

                    </div>
                  )}
                  
                  {/* Tab内容区域或直接显示年回报详情 */}
                  {(!shouldShowTabs() || activeTab === 'returns') && (
                    <div className="yearly-returns-section">

                    <div className="yearly-list">
                      {resultData.yearly_returns && resultData.yearly_returns.length > 0 ? (
                        resultData.yearly_returns.map((item, index) => {
                          const isExpanded = expandedYears.has(item.year);
                          return (
                            <div key={index} className="year-item-container" style={{ marginBottom: '20px' }}>
                              <div className="year-item-figma" onClick={() => toggleYearExpansion(item.year)} style={{ cursor: 'pointer' }}>
                                <div className="year-item-bg">
                                  <div className="year-item-border"></div>
                                  <div className="year-bookmark-icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                      <path fillRule="evenodd" clipRule="evenodd" d="M5.44885 0.666656H11.9741C14.8401 0.666656 17.1562 1.55832 17.1879 3.82499V16.475C17.1879 16.6167 17.1456 16.7583 17.061 16.8833C16.9235 17.0833 16.6909 17.2333 16.4053 17.3C16.1304 17.3667 15.8237 17.3333 15.5698 17.2167L8.71675 14.5167L1.85309 17.2167C1.69551 17.2825 1.51466 17.325 1.33488 17.325C0.742633 17.325 0.266724 16.9417 0.266724 16.475V3.82499C0.266724 1.55832 2.59339 0.666656 5.44885 0.666656ZM4.72963 7.01667H12.6932C13.1479 7.01667 13.5181 6.72417 13.5181 6.35834C13.5181 5.99167 13.1479 5.7 12.6932 5.7H4.72963C4.27487 5.7 3.90472 5.99167 3.90472 6.35834C3.90472 6.72417 4.27487 7.01667 4.72963 7.01667Z" fill="#AAAACF"/>
                                    </svg>
                                  </div>
                                  <div className="year-text">
                                    <p>{item.year}</p>
                                  </div>
                                  <div className={`year-return-rate ${(item.return_rate || 0) >= 0 ? 'positive' : 'negative'}`}>
                                    <p>{(item.return_rate || 0) >= 0 ? '+' : ''}{item.return_rate}%</p>
                                  </div>
                                  <div className={`year-dropdown-arrow ${isExpanded ? 'expanded' : ''}`}>
                                    <div className="dropdown-arrow-icon">
                                      <div className="arrow-polygon"></div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {(isExpanded || collapsingYears.has(item.year)) && (
                                <div className={`year-details-expanded ${collapsingYears.has(item.year) ? 'collapsing' : ''}`}>
                                  <div className="detail-row">
                                    <span>股价涨跌</span>
                                    <span className={item.stock_price_gain >= 0 ? 'positive' : 'negative'}>
                                      {item.stock_price_gain >= 0 ? '+' : ''}{item.stock_price_gain.toFixed(2)}%
                                    </span>
                                  </div>
                                  {/* 红框区域字段开始 - 分红相关信息显示 */}
                                  <div className="detail-row">
                                    <span>分红收益</span> {/* 红框字段1: 分红收益率显示 */}
                                    <span className="positive">{item.dividend_gain.toFixed(2)}%</span>
                                  </div>
                                  <div className="detail-row">
                                    <span>年初价格($)</span> {/* 红框字段2: 年初股价 */}
                                    <span>{item.start_price.toFixed(2)}</span>
                                  </div>
                                  <div className="detail-row">
                                    <span>年末价格($)</span> {/* 红框字段3: 年末股价 */}
                                    <span>{item.end_price.toFixed(2)}</span>
                                  </div>
                                  <div className="detail-row">
                                    <span>税前分红($)</span> {/* 红框字段4: 税前分红金额 */}
                                    <span>{item.gross_dividends.toFixed(1)}</span>
                                  </div>
                                  <div className="detail-row">
                                    <span>分红税10%</span> {/* 红框字段5: 分红税金额(10%税率) */}
                                    <span className="negative">-{item.dividend_tax.toFixed(1)}</span>
                                  </div>
                                  <div className="detail-row">
                                    <span>税后分红($)</span> {/* 红框字段6: 扣税后实际分红金额 */}
                                    <span>{item.dividends.toFixed(1)}</span>
                                  </div>
                                  {/* 红框区域字段结束 */}
                                </div>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <div className="no-data">暂无年度回报数据</div>
                      )}
                    </div>
                  </div>
                   )}
                   
                   {/* 购买记录Tab内容 */}
                   {shouldShowTabs() && activeTab === 'records' && (
                     <div 
                       className="purchase-records-section"
                       onTouchStart={handleTouchStart}
                       onTouchMove={handleTouchMove}
                       onTouchEnd={handleTouchEnd}
                     >
                       {resultData.purchase_records && resultData.purchase_records.length > 0 ? (
                         <div className="records-container">
                           {/* 购买记录表格 - 根据Figma设计调整列顺序：时间、数量、价格 */}
                           <div className="records-table">
                             <div className="table-header">
                               <div className="header-cell">时间</div>
                               <div className="header-cell">数量</div> {/* 原价格位置改为数量 */}
                               <div className="header-cell">价格($)</div> {/* 原数量位置改为价格 */}
                             </div>
                             <div className={`table-body ${(resultData.purchase_records?.length || 0) > recordsPerPage ? 'has-pagination' : ''}`}>
                               {/* 滑动动画容器 */}
                               <div className="records-swipe-container">
                                  <div 
                                    className={`records-swipe-wrapper ${isDragging ? 'dragging' : ''}`}
                                    style={{ transform: getSwipeTransform() }}
                                  >
                                   {getPaginatedRecords().map((pageRecords, pageIndex) => (
                                     <div key={pageIndex} className="records-page">
                                       {pageRecords.map((record, recordIndex) => {
                                         const globalIndex = pageIndex * recordsPerPage + recordIndex;
                                         return (
                                           <div key={globalIndex} className="table-row">
                                             <div className="table-cell">{record.date.replace(/-/g, '.')}</div> {/* 时间保持第一列，将连字符替换为点号 */}
                                             <div className="table-cell">{record.shares.toFixed(0)}</div> {/* 数量移到第二列 */}
                                             <div className="table-cell">{record.price.toFixed(2)}</div> {/* 价格移到第三列 */}
                                           </div>
                                         );
                                       })}
                                     </div>
                                   ))}
                                 </div>
                               </div>
                             </div>
                           </div>
                           {(resultData.purchase_records?.length || 0) > recordsPerPage && (
                             <div className="pagination">
                               <span className="pagination-hint">*左右滚动来查阅更多</span>
                               <span className="pagination-info">
                                  {currentPage} / {Math.ceil((resultData.purchase_records?.length || 0) / recordsPerPage)}
                                </span>
                             </div>
                           )}
                         </div>
                       ) : (
                         <div className="no-data">暂无购买记录</div>
                       )}
                     </div>
                   )}

                </div>
              </div>
            </div>
            
            {/* 返回按钮 - 居中显示 */}
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '60px', marginBottom: '20px' }}>
              <button
                onClick={handleBackToInput}
                className="calculate-button"
                style={{ width: '280px', maxWidth: '280px' }}
              >
                <div className="button-circle">
                  {/* 向上箭头图标替换原来的"↑"符号 - 用户可调整大小 */}
                  <ArrowUpIcon style={{ fontSize: '16px' }} />
                </div>
                <span className="button-text">
                  返回再算算
                </span>
              </button>
            </div>
            
            {/* Logo - 页面底部 */}
            <div className="bottom-logo">
              <LogoIcon />
            </div>
            
            {/* 底部信息文字已删除 */}
          </>
        )}
      </div>
    </div>
  );
}